import moment from "moment";
import { Popup } from "mapbox-gl";
import {
  MeteorologyMapSDK,
  configureMeteorologySdk,
} from "../vendor/meteorology/index.js";
import { getMeteorologyForecastTime } from "../api/meteorologySdk.js";
import { formatLatitudeAndLongitude } from "@/utils/lonlat.js";
// const DEFAULT_LAYER_ID = "wind";
const DEFAULT_LAYER_ID = null;
const DEFAULT_PARTICLE_STYLE = {
  particleDensity: 0.6,
  particleColor: "#ffffff",
  particleLineWidth: 1,
  particleAlpha: 1,
};
// 数据源每天在北京时间 08 时和 20 时发布预报数据
// 返回最近的已发布起报时刻（北京时间），格式 YYYYMMDDHH
function getLatestForecastStart() {
  const now = moment();
  const hour = now.hour();
  let startHour;
  if (hour < 8) {
    // 08 时之前回退到前一天 20 时
    return now.subtract(1, "day").hour(20).format("YYYYMMDDHH");
  }
  if (hour < 20) {
    // 08-20 时之间回退到当天 08 时
    startHour = 8;
  } else {
    // 20 时之后使用当天 20 时
    startHour = 20;
  }
  return now.hour(startHour).minute(0).second(0).format("YYYYMMDDHH");
}

// 实时计算回退时间数据，避免模块级常量在应用长时间运行后过期
function getFallbackForecastTimeData() {
  const start = getLatestForecastStart();
  return {
    start: [start],
    end: [moment(start, "YYYYMMDDHH").add(1, "hour").format("YYYYMMDDHH")],
  };
}
const POINT_LAYER_LABELS = {
  wind: "风场",
  temp: "温度",
  pressure: "气压",
  rainAccu: "降水",
  visibility: "能见度",
  waves: "海浪",
  currents: "洋流",
  rainaccumulationlog: "洋流",
  gale: "大风",
};
const PARTICLE_LAYER_IDS = ["wind", "waves", "rainaccumulationlog"];

let mapClickHandler = null;

function getMapInstance() {
  return window.EMAP?.map || window.map || null;
}

function resolveBeforeId(map) {
  if (!map || !map.getLayer) return undefined;
  return map.getLayer("__baselayer_end_group")
    ? "__baselayer_end_group"
    : undefined;
}

function waitForMapReady(map) {
  if (!map) return Promise.resolve();
  if (map.isStyleLoaded && map.isStyleLoaded()) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      map.off && map.off("idle", done);
      map.off && map.off("load", done);
      resolve();
    };
    map.on && map.on("idle", done);
    map.on && map.on("load", done);
  });
}

function getWasmUrl() {
  const baseUrl =
    process.env.BASE_URL || process.env.VUE_APP_PUBLIC_PATH || "/";
  return `${baseUrl.replace(/\/?$/, "/")}meteorology/meteorology-core.wasm`;
}

function normalizeLayerId(layerId) {
  return layerId === "rainaccumulationlog" ? "currents" : layerId;
}

function normalizeSdkLayerId(layerId) {
  return layerId === "currents" ? "rainaccumulationlog" : layerId;
}

function shouldEnableParticles(layerId) {
  return PARTICLE_LAYER_IDS.includes(normalizeSdkLayerId(layerId));
}

function getParticleStyle(layerId) {
  const sdkLayerId = normalizeSdkLayerId(layerId);
  return {
    particleDensity:
      sdkLayerId === "rainaccumulationlog"
        ? 0.2
        : sdkLayerId === "wind"
        ? 0.6
        : 1,
    particleColor: "#ffffff",
    particleLineWidth: 1,
    particleAlpha: 1,
  };
}

function getSdkUrls() {
  const protocol = window.location?.protocol === "https:" ? "https:" : "http:";
  return {
    imageBaseUrl: `${protocol}//meteo.ehanghai.cn`,
    category: "forecast",
  };
}

function buildTimePath(start, end) {
  if (!start || !end) return "";
  return `${String(start)}/${String(end)}`;
}

function toTimelineEntries(result) {
  const fallback = getFallbackForecastTimeData();
  const start = result?.start?.[0] || fallback.start[0];
  const endList =
    Array.isArray(result?.end) && result.end.length ? result.end : fallback.end;
  return endList.map((end, index) => ({
    index,
    start: String(start),
    end: String(end),
    path: buildTimePath(start, end),
  }));
}

function getClosestTimelineIndex(timeline) {
  if (!Array.isArray(timeline) || timeline.length === 0) return 0;
  const now = moment();
  return timeline.reduce(
    (selected, item) => {
      const date = moment(String(item.end), "YYYYMMDDHH", true);
      if (!date.isValid()) return selected;
      const diff = Math.abs(date.valueOf() - now.valueOf());
      return diff < selected.diff ? { index: item.index, diff } : selected;
    },
    { index: timeline[0].index || 0, diff: Infinity },
  ).index;
}

async function fetchForecastTime(layerId) {
  const response = await getMeteorologyForecastTime(
    normalizeSdkLayerId(layerId),
  );
  return response?.result || response?.data || getFallbackForecastTimeData();
}

function formatPointCoordinates(lng, lat) {
  const numberLng = Number(lng);
  const numberLat = Number(lat);
  if (!Number.isFinite(numberLng) || !Number.isFinite(numberLat)) {
    return {
      longitude: "--",
      latitude: "--",
    };
  }
  const { longitude, latitude } = formatLatitudeAndLongitude(
    numberLng,
    numberLat,
    {
      degreeDecimal: 4,
      minuteDecimal: 3,
      secondDecimal: 2,
    },
  );
  return {
    longitude: longitude.textCn,
    latitude: latitude.textCn,
  };
}

function formatTimePathLabel(path) {
  const [, end] = String(path || "").split("/");
  return end
    ? end.replace(/^(\d{4})(\d{2})(\d{2})(\d{2})$/, "$2-$3 $4:00")
    : "";
}

function formatPointValue(value) {
  if (
    !value ||
    !Number.isFinite(Number(value.convertedValue)) ||
    value.displayString === "No data"
  ) {
    return "-";
  }
  return value.displayString || "-";
}

function createPopupRow(label, value) {
  const row = document.createElement("div");
  row.className = "meteorology-popup-row";

  const labelEl = document.createElement("span");
  labelEl.className = "meteorology-popup-label";
  labelEl.textContent = label;

  const valueEl = document.createElement("span");
  valueEl.className = "meteorology-popup-value";
  valueEl.textContent = value;

  row.append(labelEl, valueEl);
  return row;
}

function createPointPopupContent({ layerId, valueText, lng, lat, timePath }) {
  const wrapper = document.createElement("div");
  wrapper.className = "meteorology-popup-content";

  const title = document.createElement("div");
  title.className = "meteorology-popup-title";
  title.textContent =
    POINT_LAYER_LABELS[normalizeLayerId(layerId)] ||
    POINT_LAYER_LABELS[layerId] ||
    "气象信息";
  wrapper.append(title);
  wrapper.append(createPopupRow("数值", valueText || "-"));

  const timeLabel = formatTimePathLabel(timePath);
  if (timeLabel) {
    wrapper.append(createPopupRow("时间", timeLabel));
  }

  const coordinates = formatPointCoordinates(lng, lat);
  wrapper.append(createPopupRow("经度", coordinates.longitude));
  wrapper.append(createPopupRow("纬度", coordinates.latitude));
  return wrapper;
}

function createPointPopupLoadingContent(layerId) {
  const wrapper = document.createElement("div");
  wrapper.className = "meteorology-popup-content";

  const title = document.createElement("div");
  title.className = "meteorology-popup-title";
  title.textContent =
    POINT_LAYER_LABELS[normalizeLayerId(layerId)] ||
    POINT_LAYER_LABELS[layerId] ||
    "气象信息";

  const loading = document.createElement("div");
  loading.className = "meteorology-popup-loading";
  loading.textContent = "加载中...";

  wrapper.append(title, loading);
  return wrapper;
}

function buildPopupData(layerId, lng, lat, value, timePath) {
  const coordinates = formatPointCoordinates(lng, lat);
  return {
    layerId: normalizeLayerId(layerId),
    valueText: formatPointValue(value),
    value,
    timePath: timePath || "",
    longitudeText: coordinates.longitude,
    latitudeText: coordinates.latitude,
    lng,
    lat,
  };
}

function refreshMap() {
  const map = getMapInstance();
  if (!map) return;
  map.triggerRepaint && map.triggerRepaint();
  requestAnimationFrame(() => map.triggerRepaint && map.triggerRepaint());
  setTimeout(() => {
    map.resize && map.resize();
    if (map.jumpTo) {
      map.jumpTo({
        center: map.getCenter && map.getCenter(),
        zoom: map.getZoom && map.getZoom(),
        bearing: map.getBearing && map.getBearing(),
        pitch: map.getPitch && map.getPitch(),
      });
    }
    map.triggerRepaint && map.triggerRepaint();
  }, 120);
}

function removePointPopup(state) {
  state.popupRequestId += 1;
  const popup = state.popupInstance;
  state.popupInstance = null;
  state.popupLngLat = null;
  state.popupData = null;
  popup && popup.remove && popup.remove();
}

function removeListener() {
  const map = getMapInstance();
  if (map && mapClickHandler) {
    map.off && map.off("click", mapClickHandler);
  }
  mapClickHandler = null;
}

function getPopupConstructor() {
  return Popup || window.mapboxgl?.Popup || window.maplibregl?.Popup || null;
}

function ensurePointPopup(state, lngLat) {
  const map = getMapInstance();
  const PopupCtor = getPopupConstructor();
  if (!map || !PopupCtor) return false;

  state.popupLngLat = {
    lng: Number(lngLat.lng),
    lat: Number(lngLat.lat),
  };

  if (!state.popupInstance) {
    state.popupInstance = new PopupCtor({
      className: "navPopup meteorology-popup",
      maxWidth: "none",
      offset: 8,
      closeButton: true,
    });
    state.popupInstance.on("close", () => {
      if (state.popupInstance) {
        state.popupInstance = null;
        state.popupLngLat = null;
        state.popupData = null;
        state.popupRequestId += 1;
      }
    });
  }

  state.popupInstance
    .setLngLat(state.popupLngLat)
    .setDOMContent(createPointPopupLoadingContent(state.layerId))
    .addTo(map);
  return true;
}

async function loadPointValue(state, lng, lat) {
  if (!state.sdk) return null;
  const requestId = ++state.popupRequestId;
  state.popupLngLat = { lng, lat };
  try {
    const value = await state.sdk.getPointValue(
      lng,
      lat,
      normalizeSdkLayerId(state.layerId),
    );
    if (requestId !== state.popupRequestId) return null;
    state.popupData = buildPopupData(
      state.layerId,
      lng,
      lat,
      value,
      state.timePath,
    );
    return state.popupData;
  } catch (error) {
    if (requestId !== state.popupRequestId) return null;
    state.popupData = buildPopupData(
      state.layerId,
      lng,
      lat,
      null,
      state.timePath,
    );
    return state.popupData;
  }
}

async function refreshPopupValue(state) {
  if (!state.popupLngLat) return null;
  const data = await loadPointValue(
    state,
    state.popupLngLat.lng,
    state.popupLngLat.lat,
  );
  if (data && state.popupInstance) {
    state.popupInstance.setDOMContent(
      createPointPopupContent({
        layerId: state.layerId,
        valueText: data.valueText,
        lng: state.popupLngLat.lng,
        lat: state.popupLngLat.lat,
        timePath: state.timePath,
      }),
    );
  }
  return data;
}

function updateGalePopupContent(state, lngLat, map, point) {
  const layers = ["ehh-wind-fill", "ehh-wind-point"];
  let features = [];
  try {
    features = map.queryRenderedFeatures
      ? map.queryRenderedFeatures(point, { layers })
      : [];
  } catch (e) {
    features = [];
  }

  const popup = state.popupInstance;
  if (!popup) return;

  let valueText = "-";
  let timeLabel = formatTimePathLabel(state.timePath);

  if (features && features.length > 0) {
    const feature = features[0];
    const props = feature.properties || {};
    const windLevel = props.windZLevel || props.windLevel || "-";
    const forecastTime = props.forecastTime;
    valueText = `${windLevel} 级`;
    if (forecastTime) {
      timeLabel = moment(Number(forecastTime)).format("MM-DD HH:mm");
    }
  }

  const content = document.createElement("div");
  content.className = "meteorology-popup-content";

  const title = document.createElement("div");
  title.className = "meteorology-popup-title";
  title.textContent = "大风";
  content.appendChild(title);

  content.appendChild(createPopupRow("数值", valueText));
  if (timeLabel) {
    content.appendChild(createPopupRow("时间", timeLabel));
  }
  const coordinates = formatPointCoordinates(lngLat.lng, lngLat.lat);
  content.appendChild(createPopupRow("经度", coordinates.longitude));
  content.appendChild(createPopupRow("纬度", coordinates.latitude));

  popup.setDOMContent(content);
}

export default {
  state: {
    sdk: null,
    layerId: DEFAULT_LAYER_ID,
    visible: false,
    particleEffectEnabled: false,
    timeline: [],
    timelineIndex: 0,
    loadingTimeline: false,
    timePath: "",
    opacity: 0.5,
    popupRequestId: 0,
    popupData: null,
    popupLngLat: null,
    popupInstance: null,
  },
  getters: {
    meteorologyCurrentTimelineItem: (state) =>
      state.timeline[state.timelineIndex] || null,
    meteorologyCurrentDisplayLayerId: (state) =>
      normalizeLayerId(state.layerId),
    meteorologyParticleEnabled: (state) => state.particleEffectEnabled,
  },
  mutations: {
    METEOROLOGY_SET_VISIBLE(state, visible) {
      state.visible = visible;
    },
    METEOROLOGY_SET_LAYER(state, layerId) {
      state.layerId = normalizeLayerId(layerId);
    },
    METEOROLOGY_SET_SDK(state, sdk) {
      state.sdk = sdk;
    },
    METEOROLOGY_SET_TIMELINE_LOADING(state, loading) {
      state.loadingTimeline = loading;
    },
    METEOROLOGY_SET_TIMELINE(state, timeline) {
      state.timeline = timeline;
      state.timelineIndex = getClosestTimelineIndex(timeline);
      state.timePath = timeline[state.timelineIndex]?.path || "";
    },
    METEOROLOGY_SET_TIMELINE_INDEX(state, index) {
      state.timelineIndex = index;
      state.timePath = state.timeline[index]?.path || state.timePath;
    },
    METEOROLOGY_SET_OPACITY(state, opacity) {
      state.opacity = opacity;
    },
    METEOROLOGY_SET_PARTICLES(state, enabled) {
      state.particleEffectEnabled = enabled;
    },
  },
  actions: {
    async meteorologyEnsureSdk({ state, commit }) {
      if (state.sdk) return state.sdk;
      const map = getMapInstance();
      if (!map) return null;
      await waitForMapReady(map);
      const wasmUrl = getWasmUrl();
      configureMeteorologySdk({ wasmUrl });
      const fallback = getFallbackForecastTimeData();
      const sdk = new MeteorologyMapSDK();
      sdk.init({
        map,
        layerId: normalizeSdkLayerId(state.layerId),
        opacity: state.opacity,
        beforeId: resolveBeforeId(map),
        time:
          state.timePath || buildTimePath(fallback.start[0], fallback.end[0]),
        urls: getSdkUrls(),
        wasmUrl,
        particleStyle: DEFAULT_PARTICLE_STYLE,
      });
      await sdk.ready();
      commit("METEOROLOGY_SET_SDK", sdk);
      return sdk;
    },
    async meteorologyEnsureTimeline({ commit }, layerId) {
      commit("METEOROLOGY_SET_TIMELINE_LOADING", true);
      try {
        const result = await fetchForecastTime(layerId);
        commit("METEOROLOGY_SET_TIMELINE", toTimelineEntries(result));
      } catch (error) {
        commit(
          "METEOROLOGY_SET_TIMELINE",
          toTimelineEntries(getFallbackForecastTimeData()),
        );
      } finally {
        commit("METEOROLOGY_SET_TIMELINE_LOADING", false);
      }
    },
    async meteorologyShow(
      { state, commit, dispatch },
      layerId = state.layerId,
    ) {
      commit("METEOROLOGY_SET_LAYER", layerId);

      await dispatch("meteorologyEnsureTimeline", state.layerId);
      const sdk = await dispatch("meteorologyEnsureSdk");
      if (!sdk) return;
      sdk.showLayer(normalizeSdkLayerId(state.layerId));
      sdk.setTime(state.timePath);
      if (shouldEnableParticles(state.layerId)) {
        sdk.setParticleStyle(getParticleStyle(state.layerId));
      }
      const particleEnabled = shouldEnableParticles(state.layerId);
      commit("METEOROLOGY_SET_PARTICLES", particleEnabled);
      sdk.setParticleEffectEnabled(particleEnabled);
      sdk.refresh();
      refreshMap();
      commit("METEOROLOGY_SET_VISIBLE", true);
      dispatch("meteorologyAddListener");
      if (state.popupLngLat) {
        await refreshPopupValue(state);
      }
    },
    async meteorologySwitchLayer({ state, commit, dispatch }, layerId) {
      commit("METEOROLOGY_SET_LAYER", layerId);
      await dispatch("meteorologyEnsureTimeline", state.layerId);
      const sdk = await dispatch("meteorologyEnsureSdk");
      if (!sdk) return;
      sdk.showLayer(normalizeSdkLayerId(state.layerId));
      sdk.setTime(state.timePath);
      if (shouldEnableParticles(state.layerId)) {
        sdk.setParticleStyle(getParticleStyle(state.layerId));
      }
      const particleEnabled = shouldEnableParticles(state.layerId);
      commit("METEOROLOGY_SET_PARTICLES", particleEnabled);
      sdk.setParticleEffectEnabled(particleEnabled);
      sdk.refresh();
      refreshMap();
      await refreshPopupValue(state);
    },
    async meteorologySetTimelineIndex({ state, commit }, index) {
      commit("METEOROLOGY_SET_TIMELINE_INDEX", index);
      if (!state.sdk || !state.timePath) return;
      state.sdk.setTime(state.timePath);
      state.sdk.refresh();
      refreshMap();
      await refreshPopupValue(state);
    },
    meteorologySetOpacity({ state, commit }, opacity) {
      commit("METEOROLOGY_SET_OPACITY", opacity);
      state.sdk && state.sdk.setOpacity(opacity);
      refreshMap();
    },
    meteorologyToggleParticles({ state, commit }) {
      if (!state.sdk || !shouldEnableParticles(state.layerId)) return;
      const enabled = !state.particleEffectEnabled;
      commit("METEOROLOGY_SET_PARTICLES", enabled);
      state.sdk.setParticleEffectEnabled(enabled);
    },
    meteorologySuspend({ state, commit }) {
      commit("METEOROLOGY_SET_PARTICLES", false);
      removeListener();
      removePointPopup(state);
      state.sdk && state.sdk.destroy();
      commit("METEOROLOGY_SET_SDK", null);
    },
    meteorologyRemovePopup({ state }) {
      removePointPopup(state);
    },
    meteorologyHide({ state, commit }) {
      commit("METEOROLOGY_SET_VISIBLE", false);
      commit("METEOROLOGY_SET_PARTICLES", false);
      removeListener();
      removePointPopup(state);
      state.sdk && state.sdk.destroy();
      commit("METEOROLOGY_SET_SDK", null);
    },
    meteorologyAddListener({ state, dispatch }) {
      const map = getMapInstance();
      if (!map) return;
      removeListener();
      mapClickHandler = (event) => {
        if (!state.visible) return;
        if (state.layerId === "gale") {
          dispatch("meteorologyHandleGaleClick", event);
          return;
        }
        if (!state.sdk) return;
        if (!ensurePointPopup(state, event.lngLat)) return;
        dispatch("meteorologyRefreshPopupValue");
      };
      map.on && map.on("click", mapClickHandler);
    },
    async meteorologyRefreshPopupValue({ state }) {
      await refreshPopupValue(state);
    },
    meteorologyHandleGaleClick({ state, dispatch }, event) {
      dispatch("meteorologyShowGalePopupAt", event.lngLat);
    },
    meteorologyShowGalePopupAt({ state }, lngLat) {
      const map = getMapInstance();
      if (!map || !lngLat) return;
      if (!ensurePointPopup(state, lngLat)) return;

      const point = map.project
        ? map.project([lngLat.lng, lngLat.lat])
        : { x: 0, y: 0 };
      updateGalePopupContent(state, lngLat, map, point);
    },
    meteorologyRefreshGalePopup({ state }) {
      if (!state.popupLngLat || state.layerId !== "gale") return;
      const map = getMapInstance();
      if (!map || !state.popupInstance) return;
      const point = map.project
        ? map.project([state.popupLngLat.lng, state.popupLngLat.lat])
        : { x: 0, y: 0 };
      updateGalePopupContent(state, state.popupLngLat, map, point);
    },
  },
};
